import * as React from 'react';
import {FunctionComponent} from 'react';
import {TableReportRow} from '../Models/ReportTemplate';
import {UserWithSnippets} from './Internal/UserWithSnippets';
import {reportFieldToFormattedValue, reportFieldToLabel} from '../../Models/ReportField';
import {classNames} from '../../../../Support/classNames';
import {calculateConclusions} from './Internal/conclusionsCalculator';
import {Link} from 'react-router-dom';

interface OwnProps {
    template: TableReportRow;
    usersWithSnippets: UserWithSnippets[];
    numDataColumns: number;
    selectedSnippetIds: number[];
    onChangeSelectedSnippetIds: (selectedSnippetIds: number[]) => void;
    onChangeHighlightedSnippetIds: (highlightedSnippetIds: number[]) => void;
    onDeleteSnippet: (selectedSnippetId: number) => void;
}

export const Table: FunctionComponent<OwnProps> = ({
    template,
    usersWithSnippets,
    numDataColumns,
    selectedSnippetIds,
    onChangeSelectedSnippetIds,
    onChangeHighlightedSnippetIds,
    onDeleteSnippet,
}) => {
    const numberOfRows = usersWithSnippets.reduce((acc, user) => Math.max(acc, user.snippets.length), 0);
    const snippetsRows = [...Array(numberOfRows)].map((_, index) => (
        usersWithSnippets.map((user) => (
            user.snippets[index] ? user.snippets[index].id : 0
        )
    )));

    return (
        <>
            <tr>
                {usersWithSnippets.map((publicUser) => (
                    <React.Fragment key={publicUser.publicUser.id}>
                        {template.fields.map((field, index) => {
                            return (
                                <th
                                    key={index}
                                    className={classNames({
                                        'border-end border-2': index === template.fields.length - 1,
                                    })}
                                >
                                    {reportFieldToLabel(field)}
                                </th>
                            );
                        })}
                        {numDataColumns > template.fields.length && (
                            <th colSpan={numDataColumns - template.fields.length} />
                        )}
                    </React.Fragment>
                ))}
            </tr>
            {[...Array(numberOfRows)].map((_, index) => (
                <tr 
                    key={index}
                    className="progresstable__row"
                    onMouseEnter={() => onChangeHighlightedSnippetIds(snippetsRows[index])}
                    onMouseLeave={() => onChangeHighlightedSnippetIds([])}
                >
                    {usersWithSnippets.map((publicUser) => {
                        const snippet = publicUser.snippets[index];
                        if (snippet === undefined) {
                            return (
                                <td
                                    key="empty"
                                    colSpan={numDataColumns}
                                    className="progresstable__spacer border-end border-2"
                                />
                            );
                        }
                        return (
                            <React.Fragment key={snippet?.id}>
                                {template.fields.map((field, i) => {
                                    return (
                                        <td
                                            key={field}
                                            className={classNames('progresstable__cell', {
                                                'border-end border-2': i === template.fields.length - 1,
                                            })}
                                        >
                                            <div className="d-flex justify-content-between">
                                                <span>
                                                    {i === 0 && (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedSnippetIds.includes(snippet.id)}
                                                            className={classNames(
                                                                'progresstable__checkbox progresstable__checkbox--appearing',
                                                                {
                                                                    'progresstable__checkbox--visible':
                                                                        selectedSnippetIds.length > 0,
                                                                },
                                                            )}
                                                            onChange={(e) => {
                                                                if (onChangeSelectedSnippetIds) {
                                                                    if (selectedSnippetIds.includes(snippet.id)) {
                                                                        onChangeSelectedSnippetIds([
                                                                            ...selectedSnippetIds.filter(
                                                                                (id) => id !== snippet.id,
                                                                            ),
                                                                        ]);
                                                                    } else {
                                                                        onChangeSelectedSnippetIds([
                                                                            ...selectedSnippetIds,
                                                                            snippet.id,
                                                                        ]);
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                    {reportFieldToFormattedValue(field, snippet)}
                                                </span>
                                                {i === template.fields.length - 1 && (
                                                    <span className="d-flex justify-content-end">
                                                        <button
                                                            className="btn btn-sm btn-link bi-trash progresstable__action"
                                                            onClick={() => {
                                                                if (
                                                                    confirm(
                                                                        'Are you sure you want to delete this snippet?',
                                                                    )
                                                                ) {
                                                                    onDeleteSnippet(snippet.id);
                                                                }
                                                            }}
                                                        />
                                                        <Link
                                                            to={'/snippets/' + snippet.id}
                                                            className="btn btn-link progresstable__action progresstable__action--visible"
                                                        >
                                                            <span className="bi-chevron-right" />
                                                        </Link>
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                                {numDataColumns > template.fields.length && (
                                    <td
                                        colSpan={numDataColumns - template.fields.length}
                                        className={classNames({
                                            'border-end border-2': index === template.fields.length - 1,
                                        })}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </tr>
            ))}
            <tr>
                {usersWithSnippets.map((publicUser) => {
                    return template.fields.map((field, index) => {
                        return (
                            <td
                                key={publicUser.publicUser.id + '-' + index}
                                className={classNames('bg-gray', {
                                    'border-end border-2': index === template.fields.length - 1,
                                })}
                            >
                                {calculateConclusions(field, publicUser.snippets).map((conclusion, i) => (
                                    <div key={i} style={{clear: 'both', alignmentBaseline: 'baseline'}}>
                                        <strong style={{display: 'inline-block', marginRight: 4, fontSize: '0.8em'}}>
                                            {conclusion.sign}
                                        </strong>
                                        {conclusion.formatted}
                                        {conclusion.unit && (
                                            <span style={{marginLeft: 4, fontSize: '0.8em'}}>{conclusion.unit}</span>
                                        )}
                                    </div>
                                ))}
                            </td>
                        );
                    });
                })}
            </tr>
        </>
    );
};
